import { Dialog, Switch, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import type { FormTemplateVersionDetail, FormTemplateVersionCreatePayload } from '@/types'

interface VersionCreateModalProps {
  open: boolean
  versions: FormTemplateVersionDetail[]
  submitting?: boolean
  onClose: () => void
  onSubmit: (payload: FormTemplateVersionCreatePayload) => Promise<void>
}

export function VersionCreateModal({ open, versions, submitting = false, onClose, onSubmit }: VersionCreateModalProps) {
  const [formState, setFormState] = useState<FormTemplateVersionCreatePayload>({
    displayName: '',
    description: '',
    changelog: '',
    basedOnVersionId: versions[0]?.id,
    cloneStructure: true,
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFormState({
      displayName: '',
      description: '',
      changelog: '',
      basedOnVersionId: versions[0]?.id,
      cloneStructure: true,
    })
    setError(null)
  }, [open, versions])

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  const handleSubmit = async () => {
    try {
      await onSubmit({
        displayName: formState.displayName?.trim() || undefined,
        description: formState.description?.trim() || undefined,
        changelog: formState.changelog?.trim() || undefined,
        basedOnVersionId: formState.cloneStructure ? formState.basedOnVersionId : undefined,
        cloneStructure: formState.cloneStructure,
      })
      onClose()
      setError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : '建立版本失敗'
      setError(message)
    }
  }

  const baseVersions = versions.map(version => ({
    id: version.id,
    label: `v${version.versionNumber} · ${version.status}`,
  }))

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-gray-900">建立新版本</Dialog.Title>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">版本名稱</label>
                    <input
                      type="text"
                      value={formState.displayName ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, displayName: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="例如：2025 Q1 改版"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">版本描述</label>
                    <textarea
                      value={formState.description ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="補充此版本的改動"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">變更紀錄</label>
                    <textarea
                      value={formState.changelog ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, changelog: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="說明調整細節 (選填)"
                    />
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <Switch
                      checked={Boolean(formState.cloneStructure)}
                      onChange={(value: boolean) =>
                        setFormState(prev => ({ ...prev, cloneStructure: value }))
                      }
                      className={`${formState.cloneStructure ? 'bg-primary-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${formState.cloneStructure ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                      />
                    </Switch>
                    <p className="text-sm font-medium text-gray-700">複製現有版本結構</p>
                    {formState.cloneStructure && (
                      <div>
                        <label className="block text-xs text-gray-500">來源版本</label>
                        <select
                          value={formState.basedOnVersionId ?? baseVersions[0]?.id}
                          onChange={(e) => setFormState(prev => ({ ...prev, basedOnVersionId: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        >
                          {baseVersions.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!formState.cloneStructure && (
                      <p className="text-xs text-gray-500">
                        將建立空白版本，需手動新增節點與欄位。
                      </p>
                    )}
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                    disabled={submitting}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-70"
                    disabled={submitting}
                  >
                    {submitting ? '建立中…' : '建立版本'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default VersionCreateModal
